/* eslint-disable @typescript-eslint/no-explicit-any */
import {selectSettingsUsers, settingsActions} from 'features/Settings'
import {forwardRef, useCallback, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import debounce from 'lodash.debounce'
import Select, {
  components,
  ControlProps,
  CSSObjectWithLabel,
  OnChangeValue,
  OptionProps,
  SelectInstance,
  StylesConfig,
} from 'react-select'
import {
  ExceptionType,
  SearchUsersGroupsItem,
  SliceStatus,
  UsersGroupsOption,
} from 'interfaces'
import {colorCodes, ms300} from 'definitions/constant'
import {customDropDownExceptionStyles} from 'styles/dropdown.styles'
import {useTranslation} from 'react-i18next'
import {
  formatGroupLabel,
  getSearchUsersGroupsOptionDataCy,
  getUserGroupsOptionLabel,
} from 'features/Policies/policyHelpers'
import {ChevronRightIcon} from 'components'
import 'components/Basic/mobile-overlays.css'

interface SearchMenuPosition {
  left: number
  top: number
  width: number
}

export interface SearchProps {
  callbackFunc: (val: any) => void
  classes: string
  isNoPadding: boolean
  placeholder?: string
  isSelectedCategoryCustomized: boolean
  ariaLabel?: string
}

interface SearchInternalProps extends SearchProps {
  menuPosition?: SearchMenuPosition
}

const SearchUsersGroupsControl = (
  props: ControlProps<UsersGroupsOption, false, SearchUsersGroupsItem>,
) => (
  <components.Control
    {...props}
    innerProps={
      {
        ...props.innerProps,
        'data-cy': 'search-users-groups-control',
      } as typeof props.innerProps
    }
  />
)

const SearchUsersGroupsOption = (
  props: OptionProps<UsersGroupsOption, false, SearchUsersGroupsItem>,
) => (
  <components.Option
    {...props}
    innerProps={
      {
        ...props.innerProps,
        'data-cy': getSearchUsersGroupsOptionDataCy(props.data),
      } as typeof props.innerProps
    }
  />
)
export const Search = forwardRef<
  SelectInstance<UsersGroupsOption, false, SearchUsersGroupsItem>,
  SearchInternalProps
>(
  (
    {
      callbackFunc,
      classes,
      isNoPadding,
      placeholder,
      menuPosition,
      isSelectedCategoryCustomized,
      ariaLabel = 'search',
    },
    ref,
  ) => {
    const [value, setValue] = useState('')
    const {usersGroupsList, status: searchStatus} =
      useSelector(selectSettingsUsers)

    const {t} = useTranslation(['policies'])

    const dispatch = useDispatch()

    const resetSearch = () => {
      dispatch(settingsActions.resetSearch())
      setValue('')
    }

    const onSearchUsersGroups = useCallback(
      debounce(
        (text: string) => {
          if (text.length) {
            const models = [ExceptionType.Group, ExceptionType.User]
            dispatch(
              settingsActions.getSearchUsersGroups({
                text: text,
                models,
              }),
            )
          } else {
            resetSearch()
          }
        },
        ms300,
        {leading: true},
      ),
      [],
    )

    const searchedExceptionResult =
      usersGroupsList?.length > 0 ? usersGroupsList : []

    const baseStyles: StylesConfig<
      UsersGroupsOption,
      false,
      SearchUsersGroupsItem
    > = {
      control: base => customDropDownExceptionStyles.control(base),
      menu: base => customDropDownExceptionStyles.menu(base),
      menuList: base => customDropDownExceptionStyles.menuList(base),
      option: (base, meta) => customDropDownExceptionStyles.option(base, meta),
      singleValue: base => customDropDownExceptionStyles.singleValue(base),
      dropdownIndicator: base =>
        customDropDownExceptionStyles.dropdownIndicator(base),
      input: base => customDropDownExceptionStyles.input(base),
      group: base => customDropDownExceptionStyles.group(base),
      groupHeading: base => customDropDownExceptionStyles.groupHeading(base),
    }

    const customStyles: StylesConfig<
      UsersGroupsOption,
      false,
      SearchUsersGroupsItem
    > = isNoPadding
      ? {
          ...baseStyles,
          control: (base: CSSObjectWithLabel) => ({
            ...customDropDownExceptionStyles.control({
              ...base,
              marginLeft: -10,
            }),
          }),
          menu: (base: CSSObjectWithLabel) => {
            const baseStyles = customDropDownExceptionStyles.menu({
              ...base,
            })
            if (menuPosition) {
              return {
                ...baseStyles,
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                marginTop: 0,
                marginLeft: 0,
                right: 'auto',
                border: `0.5px solid ${colorCodes.gray600} !important`,
                background: isSelectedCategoryCustomized
                  ? colorCodes.teal100
                  : colorCodes.dopeBlack,
              }
            }
            return {
              ...baseStyles,
              marginTop: 2,
              border: `0.5px solid ${colorCodes.gray600} !important`,
              background: isSelectedCategoryCustomized
                ? colorCodes.teal100
                : colorCodes.dopeBlack,
            }
          },
          input: (base: CSSObjectWithLabel) => ({
            ...base,
            color: colorCodes.dopeBlue500,
          }),
          placeholder: (base: CSSObjectWithLabel) => ({
            ...base,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }),
          menuPortal: (base: CSSObjectWithLabel) => ({
            ...base,
            zIndex: 30,
          }),
          option: (base, meta) => {
            const bgColor = isSelectedCategoryCustomized
              ? colorCodes.teal100
              : undefined
            const hoverColor = isSelectedCategoryCustomized
              ? colorCodes.teal300
              : undefined
            return {
              ...customDropDownExceptionStyles.option(
                base,
                meta,
                bgColor,
                hoverColor,
              ),
            }
          },
        }
      : {
          ...baseStyles,
          menu: (base: CSSObjectWithLabel) => ({
            ...customDropDownExceptionStyles.menu({
              ...base,
            }),
            marginTop: 1,
            border: `0.5px solid ${colorCodes.gray600} !important`,
          }),
          input: (base: CSSObjectWithLabel) => ({
            ...base,
            color: colorCodes.dopeBlue500,
          }),
        }

    return (
      <Select<UsersGroupsOption, false, SearchUsersGroupsItem>
        ref={ref}
        onChange={(val: OnChangeValue<UsersGroupsOption, false>) => {
          if (!val) {
            callbackFunc(val)
            setValue('')
            dispatch(settingsActions.resetSearch())
            return
          }

          const selectedValue =
            val.type === ExceptionType.Group ? val.name : val.emailId
          callbackFunc(val)
          setValue(selectedValue)
          dispatch(settingsActions.resetSearch())
        }}
        onInputChange={searchText => {
          onSearchUsersGroups(searchText)
        }}
        name="name"
        id="name"
        autoFocus
        value={
          value ? {name: value, emailId: value, type: ExceptionType.User} : null
        }
        placeholder={
          placeholder
            ? placeholder
            : t('BEGIN_TYPING_TO_START_FROM_AN_EXISTING_USER_GROUP')
        }
        options={searchedExceptionResult}
        formatGroupLabel={formatGroupLabel}
        getOptionLabel={options => getUserGroupsOptionLabel(options)}
        getOptionValue={options => options['emailId']}
        styles={customStyles}
        className={classes}
        // Phone sizes (44px options, 16px input) come from mobile-overlays.css.
        classNamePrefix="shared-select"
        menuPortalTarget={menuPosition ? document.body : undefined}
        menuPosition={menuPosition ? 'fixed' : undefined}
        components={{
          Control: SearchUsersGroupsControl,
          Option: SearchUsersGroupsOption,
          IndicatorSeparator: () =>
            searchStatus === SliceStatus.pending || isNoPadding ? null : (
              <ChevronRightIcon classes="mr-3" />
            ),
        }}
        noOptionsMessage={e => (e.inputValue ? 'No match found' : null)}
        isSearchable={true}
        isLoading={searchStatus === SliceStatus.pending}
        aria-label={ariaLabel}
      />
    )
  },
)

Search.displayName = 'Search'
